import type { NativeProperty } from '@/api/core/native-property-read';
import { IdText } from '@/components/id-text';
import { RowLink } from '@/components/row-link';
import { formatDateTimeWithOffset } from '@/lib/format';
import { Badge, DataTable, type DataTableColumn } from '@mgrvtechgroup/admin-ds';
import type { LucideIcon } from 'lucide-react';
import { nativePropertyNameHint } from './native-property-url';

/**
 * 운영 지점 표. 서버가 지점명 순서만 정렬하므로 지점명 머리글만 정렬 가능하고,
 * 방향만 바꾼다(정렬 해제는 부모가 기본 오름차순으로 되돌린다).
 * 행 전체가 하나의 링크다 — 이름 링크의 클릭 영역을 행 전체로 넓혀(`stretched`, 행은 `relative`) 어느 칸에 올려도
 * 브라우저가 주소를 보여 주고 ⌘/Ctrl·가운데 클릭, 우클릭 "새 탭에서 열기"가 된다. Tab으로 가는 곳은 행마다 이름 링크 하나다.
 */
export function NativePropertyTable({
  rows,
  isLoading,
  direction,
  empty,
  detailHref,
  onSortChange,
  onOpen,
}: {
  rows: NativeProperty[];
  isLoading: boolean;
  direction: 'asc' | 'desc';
  empty: { title: string; description: string; icon: LucideIcon };
  /** 상세 주소 — 목록으로 돌아올 때 같은 페이지·정렬·검색 조건이 남도록 복귀 주소를 싣는다. */
  detailHref: (id: string) => string;
  onSortChange: (sort: string | null) => void;
  onOpen: (row: NativeProperty) => void;
}) {
  const columns: DataTableColumn<NativeProperty>[] = [
    {
      key: 'name',
      header: '지점명',
      // 이름 셀은 진짜 링크 — Tab으로 들어가 Enter로 열고, 클릭 영역은 행 전체다.
      // 이름을 함께 보내, 상세가 지점 정보를 읽는 동안에도 경로에 이름을 바로 보여 주게 한다.
      cell: (row) => (
        <RowLink to={detailHref(row.id)} state={nativePropertyNameHint(row)} stretched>
          {row.name}
        </RowLink>
      ),
    },
    { key: 'code', header: '코드', sortable: false, cell: (row) => row.code },
    {
      key: 'application_enabled',
      header: '신청 허용',
      sortable: false,
      cell: (row) => (
        <Badge tone={row.application_enabled ? 'success' : 'neutral'}>
          {row.application_enabled ? '허용' : '차단'}
        </Badge>
      ),
    },
    {
      key: 'created_at',
      header: '등록 시각',
      sortable: false,
      cellClassName: 'tabular-nums text-muted-foreground',
      cell: (row) => (
        <time dateTime={row.created_at}>{formatDateTimeWithOffset(row.created_at)}</time>
      ),
    },
    {
      key: 'id',
      header: '지점 ID',
      sortable: false,
      // 복사 영역 — 행 링크 위로 올려(z-10) 이 안의 클릭은 이동하지 않는다. ID는 끌어서 선택하거나 복사 버튼으로 복사한다.
      cell: (row) => (
        <span
          className="relative z-10 inline-flex"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <IdText value={row.id} label={`${row.name} 지점 ID`} />
        </span>
      ),
    },
  ];
  return (
    <DataTable
      columns={columns}
      data={rows}
      rowKey={(row) => row.id}
      // 이름 링크의 넓힌 클릭 영역이 이 행 안에 머문다.
      rowClassName={() => 'relative'}
      isLoading={isLoading}
      sort={`name,${direction}`}
      onSortChange={onSortChange}
      onRowClick={onOpen}
      empty={empty}
    />
  );
}
